// Classic header (Navigation). A view over core's useSiteNav() (§4.5): the
// gated built-in links, the per-host-and-sport cached /public/nav-menu items
// (refetched on a sport change), safeHref sanitising and the active-link rule
// all live in core/chrome/useSiteNav. This file keeps today's markup and its
// presentation-only state (scroll tint, mobile drawer, body scroll lock).

import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useBodyOverflow } from '@/hooks/useBodyOverflow'
import SportSwitcher from '@/components/SportSwitcher'
import { pinnedNavItem, useSiteNav } from '@/site/core/chrome/useSiteNav'
import { sf } from '@/site/core/features/sf'
import { useT } from '@/i18n'
import { siteClassicMessages } from '@/i18n/catalogs/site-classic'
import { splitNavLinks } from './navOverflow'
import { useNavOverflow } from './useNavOverflow'
import { AccountMenu, MobileAccount } from './AccountMenu'
import { useClassicFocusRing } from './useClassicFocusRing'

export default function Navigation({ noSpacer = false }: { noSpacer?: boolean } = {}) {
  useClassicFocusRing()
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  // Core nav data. Called before this view's own effects, so the nav-menu
  // fetch keeps its place as the header's first effect (§5.2).
  const nav = useSiteNav()
  const location = useLocation()
  const c = useT(siteClassicMessages).chrome
  const tenantName = nav.leagueName
  const tenantLogo = nav.logoUrl
  // Registration add-on only (null otherwise: no element, class or hook).
  const account = nav.account

  // Handle scroll for background opacity
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close menu on route change
  useEffect(() => {
    setIsOpen(false)
  }, [location.pathname])

  // Prevent body scroll when menu is open
  useBodyOverflow(isOpen)

  const isActive = nav.isActive

  // Static + league menu links, already gated by site section and sanitised.
  // QA E1: on a members-only league a visitor the wall stops gets Register
  // only (nav.restricted), and no sport switcher.
  const allNavLinks = nav.items

  // ─── Desktop overflow (#417) ────────────────────────────────────────────
  // A row that fits renders exactly as before; one that genuinely overflows
  // collapses its tail into "More" so the sport switcher stays on screen.
  // Register never does (core's pinnedNavItem). The rules (observe, never
  // collapse on a transient) are in useNavOverflow.
  const pinned = pinnedNavItem(nav)
  const pinnedIndex = pinned ? allNavLinks.indexOf(pinned) : -1
  const { rowRef, logoRef, groupRef, switcherRef, moreRef, shown } = useNavOverflow(allNavLinks, pinnedIndex)
  const [moreOpen, setMoreOpen] = useState(false)

// The More menu closes on navigation, outside clicks and Escape.
  useEffect(() => {
    setMoreOpen(false)
  }, [location.pathname])
  useEffect(() => {
    if (!moreOpen) return
    const onDown = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [moreOpen, moreRef])

  const { row: desktopLinks, more: moreLinks } = splitNavLinks(allNavLinks, shown, pinnedIndex)

  return (
    <>
      {/* Skip link (#822): hidden until focused, jumps past the nav. */}
      <a
        href="#site-main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[60] focus:px-4 focus:py-2 focus:bg-bg-secondary focus:text-white focus:text-sm focus:font-bold focus:uppercase focus:tracking-wider"
      >
        {c.skipToContent}
      </a>
      {/* Navigation Bar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || isOpen
            ? 'bg-bg-primary/95 backdrop-blur-md border-b border-secondary/30'
            : // Transparent over the homepage hero photo (noSpacer): keep the
              // links white even on a light theme so they read on the image (#193).
              `bg-transparent${noSpacer ? ' keep-white' : ''}`
        }`}
        {...sf('site.header')}
      >
        <div className="container mx-auto px-4">
          <div ref={rowRef} className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            {/* #630: a league with no logo shows only the (phone-hidden) name; the link keeps a name. */}
            <Link ref={logoRef} to="/" className="flex items-center gap-3 group" aria-label={tenantLogo ? undefined : tenantName}>
              {tenantLogo ? (
                // max-w-none: with the reset's max-width: 100% the image adds nothing to
                // this link's minimum width, so a squeezed row ran the name into Home.
                <img
                  src={tenantLogo}
                  alt={tenantName}
                  className="w-10 h-10 lg:w-12 lg:h-12 max-w-none object-contain"
                />
              ) : null}
              <div className="hidden sm:flex flex-col">
                <span className="text-white font-['Montserrat'] font-black uppercase tracking-tight text-base lg:text-lg leading-none group-hover:text-primary transition-colors">
                  {tenantName}
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div ref={groupRef} className="hidden lg:flex items-center gap-1">
              {desktopLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-all ${
                    isActive(link.href)
                      ? 'text-primary'
                      : 'text-white/80 hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {/* More — only when the links don't fit (#417). */}
              {moreLinks.length > 0 && (
                <div ref={moreRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setMoreOpen((o) => !o)}
                    aria-haspopup="true"
                    aria-expanded={moreOpen}
                    aria-controls="site-nav-more"
                    className={`flex items-center gap-1 px-4 py-2 text-sm font-bold uppercase tracking-wider transition-all ${
                      moreLinks.some((l) => isActive(l.href)) ? 'text-primary' : 'text-white/80 hover:text-white'
                    }`}
                  >
                    {c.more}
                    <svg className={`w-3 h-3 transition-transform ${moreOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {moreOpen && (
                    <div
                      id="site-nav-more"
                      className="absolute right-0 top-full mt-2 min-w-[14rem] max-h-[70vh] overflow-y-auto bg-bg-secondary border border-secondary/30 rounded-lg shadow-2xl py-2"
                    >
                      {moreLinks.map((link) => (
                        <Link
                          key={link.href}
                          to={link.href}
                          onClick={() => setMoreOpen(false)}
                          className={`block px-4 py-2 text-sm font-bold uppercase tracking-wider transition-all ${
                            isActive(link.href) ? 'text-primary' : 'text-white/80 hover:text-white hover:bg-white/5'
                          }`}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* Sport switcher — renders only on multi-sport tenants (#35).
                  The account control sits in the same measured wrapper, so
                  useNavOverflow accounts for it (no new observed element). */}
              <div ref={switcherRef} className={`ml-2 pl-2 border-l border-secondary/40${account ? ' flex items-center gap-3' : ''}`}>
                {!nav.restricted && <SportSwitcher />}
                {account && <AccountMenu account={account} />}
              </div>
            </div>

            {/* Mobile Hamburger Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden relative w-10 h-10 flex items-center justify-center"
              aria-label={c.toggleMenu}
            >
              <div className="w-6 flex flex-col gap-1.5">
                <span
                  className={`block h-0.5 bg-white transition-all duration-300 origin-center ${
                    isOpen ? 'rotate-45 translate-y-2' : ''
                  }`}
                />
                <span
                  className={`block h-0.5 bg-white transition-all duration-300 ${
                    isOpen ? 'opacity-0 scale-0' : ''
                  }`}
                />
                <span
                  className={`block h-0.5 bg-white transition-all duration-300 origin-center ${
                    isOpen ? '-rotate-45 -translate-y-2' : ''
                  }`}
                />
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          isOpen ? 'visible' : 'invisible'
        }`}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-bg-primary/80 backdrop-blur-sm transition-opacity duration-300 ${
            isOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setIsOpen(false)}
        />

        {/* Slide-out Menu */}
        <div
          className={`absolute top-0 right-0 w-72 h-full flex flex-col overflow-y-auto overscroll-contain bg-bg-secondary border-l border-secondary/30 transition-transform duration-300 ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="pt-20 px-6">
            {/* Sport switcher (mobile) — renders only on multi-sport tenants. */}
            <div className="mb-4">
              {!nav.restricted && <SportSwitcher compact />}
            </div>

            {account && <MobileAccount account={account} />}

            {/* Mobile Nav Links */}
            <div className="space-y-2">
              {allNavLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`block px-4 py-3 text-base font-bold uppercase tracking-wider transition-all border-l-2 ${
                    isActive(link.href)
                      ? 'text-primary border-primary bg-primary/5'
                      : 'text-white/80 border-transparent hover:text-white hover:border-white/30 hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Footer Info — in flow, so a long menu scrolls instead of
              running under it (#780). */}
          <div className="mt-auto px-6 pt-6 pb-[calc(2rem+env(safe-area-inset-bottom,0px))]">
            <div className="flex items-center gap-3">
              {tenantLogo ? (
                <img
                  src={tenantLogo}
                  alt={tenantName}
                  className="w-8 h-8 object-contain opacity-50"
                />
              ) : null}
              <span className="text-white/60 text-xs">
                © {new Date().getFullYear()} {tenantName}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Spacer to prevent content from going under fixed nav */}
      {!noSpacer && <div className="h-16 lg:h-20" />}
      {/* Skip-link target: the page content starts here. */}
      <div id="site-main" tabIndex={-1} className="outline-none scroll-mt-16 lg:scroll-mt-20" />
    </>
  )
}
